// Gemeinsame Bausteine der Suite-Oberfläche.
//
// Jede Seite setzt sich aus denselben Teilen zusammen: Kopf (PageHeader),
// Karten (Card), Kennzahlen (StatTile), Etiketten (Badge), Knöpfe (Button),
// Hinweise (Callout) und leere Zustände (EmptyState). Die Optik steht in
// src/index.css (@layer components); hier nur die Form.
//
// Wer eine Seite überarbeitet, nimmt diese Teile statt eigener Klassenketten –
// so sieht die Suite überall gleich aus, und eine Änderung wirkt überall.

import type { ButtonHTMLAttributes, ReactNode } from "react";

type Ton = "neutral" | "brand" | "success" | "warning" | "danger" | "info";

/** Kopf einer Seite: Titel, eine Zeile Erklärung, rechts die Hauptaktionen. */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Kleine Zeile über dem Titel, z. B. der Bereich („Sendebetrieb“). */
  eyebrow?: ReactNode;
  /** Platz unter dem Kopf, z. B. für Reiter. */
  children?: ReactNode;
}): JSX.Element {
  return (
    <header className="mb-6 animate-fade-in">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <div className="section-label mb-1.5">{eyebrow}</div>}
          <h1 className="text-[1.65rem] font-semibold leading-tight tracking-tight text-primary">{title}</h1>
          {description && <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-5">{children}</div>}
    </header>
  );
}

/** Innenabstand einer Seite – überall gleich. */
export function Page({ children, className = "" }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={`px-4 py-6 sm:px-6 lg:px-8 lg:py-8 ${className}`}>{children}</div>;
}

export function Card({
  title,
  description,
  actions,
  children,
  className = "",
  padded = true,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** false: Inhalt bis an den Rand (Tabellen, Listen). */
  padded?: boolean;
}): JSX.Element {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <div className="card-header">
          <div className="min-w-0">
            {title && <h2 className="card-title">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={padded ? "card-body" : ""}>{children}</div>
    </section>
  );
}

export function Button({
  variant = "secondary",
  size,
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "lg";
}): JSX.Element {
  return (
    <button
      type="button"
      className={`btn btn-${variant} ${size ? `btn-${size}` : ""} ${className}`}
      {...rest}
    />
  );
}

export function Badge({ tone = "neutral", dot = false, children, className = "" }: {
  tone?: Ton;
  dot?: boolean;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <span className={`badge badge-${tone} ${className}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** Kennzahl mit Beschriftung; optional Trend und Symbol. */
export function StatTile({
  label,
  value,
  unit,
  hint,
  tone = "neutral",
  icon,
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  hint?: ReactNode;
  tone?: Ton;
  icon?: ReactNode;
}): JSX.Element {
  const akzent: Record<Ton, string> = {
    neutral: "text-primary",
    brand: "text-brand-primary",
    success: "text-emerald-600 dark:text-emerald-400",
    warning: "text-amber-600 dark:text-amber-400",
    danger: "text-red-600 dark:text-red-400",
    info: "text-sky-600 dark:text-sky-400",
  };
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.8rem] font-medium text-muted">{label}</span>
        {icon && <span className="text-muted">{icon}</span>}
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className={`text-[1.75rem] font-semibold leading-none tracking-tight tabular-nums ${akzent[tone]}`}>{value}</span>
        {unit && <span className="text-sm text-muted">{unit}</span>}
      </div>
      {hint && <div className="mt-2 text-xs text-muted">{hint}</div>}
    </div>
  );
}

export function Callout({ tone = "info", title, children, className = "" }: {
  tone?: "info" | "warning" | "danger" | "success";
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={`callout callout-${tone} ${className}`} role={tone === "danger" ? "alert" : undefined}>
      <div className="min-w-0">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={title ? "mt-0.5 opacity-90" : ""}>{children}</div>}
      </div>
    </div>
  );
}

/** Wenn es (noch) nichts zu zeigen gibt: sagen, was man tun kann. */
export function EmptyState({ icon, title, description, action }: {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-default px-6 py-12 text-center">
      {icon && (
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">{icon}</div>
      )}
      <div className="text-sm font-semibold text-primary">{title}</div>
      {description && <p className="mt-1 max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Reiter als Segmentschalter. */
export function Tabs<T extends string>({ value, onChange, items, className = "" }: {
  value: T;
  onChange: (v: T) => void;
  items: Array<{ value: T; label: ReactNode; count?: number }>;
  className?: string;
}): JSX.Element {
  return (
    <div className={`tabbar ${className}`} role="tablist">
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={value === it.value}
          onClick={() => onChange(it.value)}
          className={`tab ${value === it.value ? "tab-active" : ""}`}
        >
          {it.label}
          {it.count !== undefined && it.count > 0 && (
            <span className="ml-1.5 rounded-full bg-brand-primary/15 px-1.5 text-[0.7rem] font-semibold text-brand-primary">{it.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Roter „ON AIR“-Punkt mit ruhigem Puls. */
export function LiveDot({ className = "" }: { className?: string }): JSX.Element {
  return <span className={`inline-block h-2 w-2 flex-shrink-0 animate-live-pulse rounded-full bg-red-500 ${className}`} aria-hidden="true" />;
}

/** Kleine Pegelanimation (drei Balken) für „läuft gerade“. */
export function EqBars({ className = "" }: { className?: string }): JSX.Element {
  return (
    <span className={`inline-flex h-3.5 items-end gap-[2px] ${className}`} aria-hidden="true">
      {[0, 0.2, 0.4].map((d) => (
        <span key={d} className="w-[3px] origin-bottom animate-eq rounded-sm bg-current" style={{ height: "100%", animationDelay: `${d}s` }} />
      ))}
    </span>
  );
}

/** Platzhalter in Form des späteren Inhalts. */
export function Skeleton({ className = "" }: { className?: string }): JSX.Element {
  return <div className={`animate-pulse rounded-lg bg-input ${className}`} />;
}
